import { KeyboardEvent, ReactNode, useRef } from "react";
import s from "./Tabs.module.scss";

type Item = { id: string; label: ReactNode; count?: number };
type Props = { label: string; items: Item[]; active: string; onChange: (id: string) => void };
/**
 * Tabs with a panel: each tab gets the id `tabId(prefix, item)` and `aria-controls` the panel; the panel (the
 * caller's) carries `role="tabpanel"`, `id={panel}` and `aria-labelledby={tabId(prefix, active)}`.
 */
type PanelIds = { prefix: string; panel: string };
export const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;

/** WAI-ARIA tabs pattern: roving tabIndex; ←/→ (swapped in RTL), Home, End move and select. */
export const Tabs = ({ label, items, active, onChange, ids }: Props & { ids?: PanelIds }) => {
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const count = items.length;
    const idx = items.findIndex((it) => it.id === active);
    const rtl = document.documentElement.dir === "rtl";
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (idx + (rtl ? -1 : 1) + count) % count;
    else if (e.key === "ArrowLeft") next = (idx + (rtl ? 1 : -1) + count) % count;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = count - 1;
    if (next === null) return;
    e.preventDefault();
    const id = items[next].id;
    onChange(id);
    buttons.current[id]?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className={s.tabs} onKeyDown={onKeyDown}>
      {items.map((it) => (
        <button key={it.id} type="button" role="tab" aria-selected={it.id === active} tabIndex={it.id === active ? 0 : -1}
          id={ids ? tabId(ids.prefix, it.id) : undefined} aria-controls={ids?.panel}
          ref={(el) => { buttons.current[it.id] = el; }}
          className={`${s.tab} ${it.id === active ? s.active : ""}`} onClick={() => onChange(it.id)}>
          {it.label}{typeof it.count === "number" && <span className={s.count}>{it.count}</span>}
        </button>
      ))}
    </div>
  );
};

export const Segment = ({ label, items, active, onChange }: Props) => (
  <div role="group" aria-label={label} className={s.segment}>
    {items.map((it) => (
      <button key={it.id} type="button" aria-pressed={it.id === active} className={`${s.seg} ${it.id === active ? s.on : ""}`} onClick={() => onChange(it.id)}>
        {it.label}{typeof it.count === "number" && <span className={s.count}>{it.count}</span>}
      </button>
    ))}
  </div>
);
